<template>
  <div class="c-option-chain-quote">
    <div class="d-flex">
      <div class="d-flex flex-column">
        <div class="d-flex ticker-box">
          <div class="title-row mt-3 mb-3">
            <v-flag class="mr-2" :width="24" :countryCode="underlyingData['q._COUNTRY'] || ''">
              <slot v-if="underlyingData['q._COUNTRY']">{{
                $t('white-label.countries.' + underlyingData['q._COUNTRY'])
              }}</slot>
            </v-flag>
            <span class="field-ric mr-2"> {{ baseRIC }} </span>
            <span class="field-display-name"> {{ underlyingData['q._DSPLY_NAME'] }} </span>
            <c-halt-indicator :tradeHaltValue="underlyingData['q.TRD_STATUS']" :toolTipRequired="true" />
          </div>
        </div>
        <div class="pricing-row mb-3">
          <div class="d-flex align-items-baseline mr-4">
            <c-trend-arrow
              :class="`mr-1 ${getChangeClass(underlyingData['q._NETCHNG_1'])}`"
              :orientation="getTrend(underlyingData['q._NETCHNG_1'])"
            />
            <c-tick-flash
              :class="`field-price mr-2 ${getChangeClass(underlyingData['q._NETCHNG_1'])}`"
              :value="underlyingData['q._TRDPRC_1']"
            >
              {{ $f(underlyingData['q._TRDPRC_1'], underlyingMetadata['q._TRDPRC_1']) }}
            </c-tick-flash>
            <c-tick-flash
              :class="`field-change mr-2 ${getChangeClass(underlyingData['q._NETCHNG_1'])}`"
              :value="underlyingData['q._NETCHNG_1']"
            >
              {{ $f(underlyingData['q._NETCHNG_1'], underlyingMetadata['q._NETCHNG_1']) }}
            </c-tick-flash>
            <c-tick-flash
              :class="`field-change ${getChangeClass(underlyingData['q._PCTCHNG'])}`"
              :value="underlyingData['q._PCTCHNG']"
            >
              {{ $f(underlyingData['q._PCTCHNG'], underlyingMetadata['q._PCTCHNG']) }}
            </c-tick-flash>
          </div>
          <c-action-buttons-bar
            :buy="() => $emit('buy-clicked')"
            :sell="() => $emit('sell-clicked')"
            :cover="() => $emit('cover-clicked')"
            :short="() => $emit('short-clicked')"
            :alert="() => $emit('alert-clicked')"
            :wid="wid"
          />
        </div>
      </div>
      <div class="upper-flex">
        <div class="mt-3 mb-3 earnings-row">
          <div class="highlight">{{ $t(`white-label.widgets.optionChain.labels.nextEarnings`) }}</div>
          <div class="d-flex">
            <div v-if="displayedEarningsDate">
              {{ displayedEarningsDate }}
            </div>
            <div class="market-phase" v-if="nextMarketPhase">
              {{ $t(`white-label.widgets.optionChain.marketPhaseMap.${nextMarketPhase}`) }}
            </div>
          </div>
        </div>
        <div class="analyst-row">
          <div class="label">{{ $t(`white-label.widgets.optionChain.labels.analystAverage`) }}</div>
          <div class="value" :class="underlyingData['recomText']" v-if="underlyingData['recomText']">
            {{ $t(`white-label.widgets.optionChain.labels.${underlyingData['recomText']}`) }}
          </div>
          <div class="value grey-backg" v-else>
            -
          </div>
          <div class="label">{{ $t(`white-label.widgets.optionChain.labels.targetPrice`) }}</div>
          <div class="value" :class="targetPriceClass">
            {{ $f(underlyingData['rkd4.TARGETPRICE'], 'float.price') }}
          </div>
        </div>
      </div>
    </div>
    <div class="quotes-row d-flex" :class="viewMode" v-resize:throttle.100="setWidgetViewMode">
      <div class="clickable mr-4" :class="getBuySellClasses('bid')" @click="openOrderEntryForBidAsk('BID')">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.bid-size') }}</span>
        <span class="d-flex">
          <c-tick-flash :value="underlyingData['q._BID']">
            <span>{{ $f(underlyingData['q._BID'], underlyingMetadata['q._BID']) }}</span>
          </c-tick-flash>
          <c-tick-flash :value="underlyingData['q.BIDSIZE']">
            <span class="ml-1">({{ $f(underlyingData['q.BIDSIZE'], underlyingMetadata['q.BIDSIZE']) }})</span>
          </c-tick-flash>
        </span>
        <span class="sr-only">{{ $t('white-label.globalLabels.buttonAria') }}</span>
      </div>
      <div class="clickable mr-4" :class="getBuySellClasses('ask')" @click="openOrderEntryForBidAsk('ASK')">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.ask-size') }}</span>
        <div class="d-flex">
          <c-tick-flash :value="underlyingData['q._ASK']">
            <span>{{ $f(underlyingData['q._ASK'], underlyingMetadata['q._ASK']) }}</span>
          </c-tick-flash>
          <c-tick-flash :value="underlyingData['q.ASKSIZE']">
            <span class="ml-1">({{ $f(underlyingData['q.ASKSIZE'], underlyingMetadata['q.ASKSIZE']) }})</span>
          </c-tick-flash>
        </div>
        <span class="sr-only">{{ $t('white-label.globalLabels.buttonAria') }}</span>
      </div>
      <div class="mr-4">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.open-close') }}</span>
        <div class="d-flex">
          <c-tick-flash :value="underlyingData['q._OPEN_PRC']">
            <span>{{ $f(underlyingData['q._OPEN_PRC'], underlyingMetadata['q._OPEN_PRC']) }}</span>
          </c-tick-flash>
          <span class="pl-1 pr-1">/</span>
          <c-tick-flash :value="underlyingData['q._HST_CLOSE']">
            <span>{{ $f(underlyingData['q._HST_CLOSE'], underlyingMetadata['q._HST_CLOSE']) }}</span>
          </c-tick-flash>
        </div>
      </div>
      <div class="mr-4">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.high-low') }}</span>
        <div class="d-flex">
          <c-tick-flash :value="underlyingData['q.YRHIGH']">
            <span>{{ $f(underlyingData['q._HIGH_1'], underlyingMetadata['q._HIGH_1']) }}</span>
          </c-tick-flash>
          <span class="pl-1 pr-1">/</span>
          <c-tick-flash :value="underlyingData['q._LOW_1']">
            <span>{{ $f(underlyingData['q._LOW_1'], underlyingMetadata['q._LOW_1']) }}</span>
          </c-tick-flash>
        </div>
      </div>
      <div class="mr-4">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.52-high-low') }}</span>
        <div class="d-flex">
          <c-tick-flash :value="underlyingData['q.YRHIGH']">
            <span>{{ $f(underlyingData['q.YRHIGH'], underlyingMetadata['q.YRHIGH']) }}</span>
          </c-tick-flash>
          <span class="pl-1 pr-1">/</span>
          <c-tick-flash :value="underlyingData['q.YRLOW']">
            <span>{{ $f(underlyingData['q.YRLOW'], underlyingMetadata['q.YRLOW']) }}</span>
          </c-tick-flash>
        </div>
      </div>
      <div class="mr-4">
        <span class="field-title">{{ i18n('q._ACVOL_1').full }}</span>
        <div class="d-flex">
          <span>{{ $f(underlyingData['q._ACVOL_1'], underlyingMetadata['q._ACVOL_1']) }}</span>
        </div>
      </div>
      <div class="mr-4">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.per-eps') }}</span>
        <div class="d-flex">
          <c-tick-flash :value="underlyingData['rkd.PRICE_TO_EPS']">
            <span>{{ $f(underlyingData['rkd.PRICE_TO_EPS'], underlyingMetadata['rkd.PRICE_TO_EPS']) }}</span>
          </c-tick-flash>
          <span class="pl-1 pr-1">/</span>
          <c-tick-flash :value="underlyingData['rkd.EPS']">
            <span>{{ $f(underlyingData['rkd.EPS'], underlyingMetadata['rkd.EPS']) }}</span>
          </c-tick-flash>
        </div>
      </div>
      <div class="mr-4">
        <span class="field-title">{{ $t('white-label.widgets.optionChain.labels.dps-price') }}</span>
        <td class="d-flex">
          <c-tick-flash :value="underlyingData['q.DIVIDEND']">
            <span>{{ $f(underlyingData['q.DIVIDEND'], underlyingMetadata['q.DIVIDEND']) }}</span>
          </c-tick-flash>
          <span class="pl-1 pr-1">/</span>
          <c-tick-flash :value="underlyingData['q._YIELD']">
            {{ $f(underlyingData['q._YIELD'], underlyingMetadata['q._YIELD']) }}
          </c-tick-flash>
        </td>
      </div>
    </div>
  </div>
</template>

<script>
import CTrendArrow from '@custom/CTrendArrow';
import CTickFlash from '@custom/CTickFlash';
import CActionButtonsBar from '@custom/CActionButtonsBar';
import CHaltIndicator from '@custom/CHaltIndicator';
import resize from 'vue-resize-directive';

export default {
  name: 'c-option-chain-quote',
  components: {
    CTrendArrow,
    CTickFlash,
    CActionButtonsBar,
    CHaltIndicator
  },
  directives: {
    resize
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    underlyingData: {
      type: Object,
      required: true
    },
    underlyingMetadata: {
      type: Object,
      required: true
    },
    displayedEarningsDate: {
      required: true
    },
    nextMarketPhase: {
      required: true
    }
  },
  computed: {
    ric() {
      return this.underlyingData['q.RIC'];
    },
    baseRIC() {
      return this.underlyingData['q.RIC'] ? this.underlyingData['q.RIC'].split('.')[0] : '-';
    },
    buySellAtType() {
      return this.$store.state.applicationSettings.orders.settings.stocks.buySellAtType;
    },
    targetPriceClass() {
      if (this.underlyingData['rkd4.TARGETPRICE'] === '-' || this.underlyingData['rkd4.TARGETPRICE'] === '') {
        return 'grey-backg';
      }
      return this.underlyingData['q._TRDPRC_1'] < this.underlyingData['rkd4.TARGETPRICE'] ? 'positive' : 'negative';
    }
  },
  data() {
    return {
      viewMode: ''
    };
  },
  methods: {
    i18n(fid) {
      return this.$t(`white-label.globalLabels.fids.${fid.replace('.', '|')}`);
    },
    getChangeClass(value) {
      return value > 0 ? 'positive-change' : value < 0 ? 'negative-change' : 'neutral-change';
    },
    getTrend(value) {
      return value > 0 ? 'up' : value < 0 ? 'down' : 'right';
    },
    openOrderEntryForBidAsk(value) {
      let action = '';
      if (value === 'ASK') {
        if (this.buySellAtType === 'buyAtAsk') {
          action = 'BUY';
        } else {
          action = 'SELL';
        }
      } else if (value === 'BID') {
        if (this.buySellAtType === 'buyAtBid') {
          action = 'BUY';
        } else {
          action = 'SELL';
        }
      }
      this.$widget.openOrderEntry({
        orderMode: { action: action },
        legs: {
          firstOrder: {
            symbol: { ric: this.ric },
            underlying: { ric: this.ric }
          }
        }
      });
    },
    getBuySellClasses(action) {
      return (this.buySellAtType === 'buyAtAsk' && action === 'ask') ||
        (this.buySellAtType === 'buyAtBid' && action === 'bid')
        ? 'column-buy'
        : 'column-sell';
    },
    setWidgetViewMode() {
      if (this.$el) this.viewMode = this.$el.getBoundingClientRect().width < 700 ? 'flex-start' : 'flex-space-between';
    }
  },
  mounted() {
    this.setWidgetViewMode();
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-option-chain-quote';
</style>
